import axios from "axios";
import { TokenStorage } from "./tokenStorage";
import { userLogClient } from "../shared/lib/userLog/userLogClient";
import { LogAction, LogResultStatus } from "../shared/lib/userLog/types";
import { emitSessionExpired } from "../shared/lib/sessionEvents";
import { sessionHint } from "../shared/lib/sessionHint";
import { getHttpStatus } from "../shared/lib/httpStatus";

const baseURL = import.meta.env.VITE_BACKEND_URL;
let tokenRefreshPromise: Promise<string> | null = null;

export const axiosInstance = axios.create({
	baseURL,
	headers: {
		"Content-Type": "application/json",
	},
	withCredentials: true, // Include cookies in requests
});

// Request interceptor to add access token to requests
axiosInstance.interceptors.request.use(
	(config) => {
		const accessToken = TokenStorage.getAccessToken();
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		// Record the start time for loading_time instrumentation on response.
		(config as any).metadata = { startedAt: performance.now() };
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Response interceptor - token refresh is handled by AuthContext; here we
// ONLY observe failures and emit a frontend api_error log (fire-and-forget)
// so client-visible failures (network errors, timeouts, 4xx/5xx from the
// user's perspective) are queryable alongside backend logs.
axiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error?.config as (typeof error.config & { _retry?: boolean }) | undefined;
		const isRefreshRequest = originalRequest?.url?.endsWith("/auth/refresh");

		// Access tokens expire after one hour. Refresh once and replay the
		// original request so an active remembered session is uninterrupted.
		if (
			error?.response?.status === 401 &&
			originalRequest &&
			!originalRequest._retry &&
			!isRefreshRequest &&
			TokenStorage.getAccessToken()
		) {
			originalRequest._retry = true;

			if (!tokenRefreshPromise) {
				tokenRefreshPromise = axiosInstance
					.post("/auth/refresh")
					.then((response) => {
						const accessToken = response.data.accessToken as string;
						TokenStorage.setAccessToken(accessToken);
						return accessToken;
					})
					.finally(() => {
						tokenRefreshPromise = null;
					});
			}

			try {
				const accessToken = await tokenRefreshPromise;
				originalRequest.headers.Authorization = `Bearer ${accessToken}`;
				return axiosInstance(originalRequest);
			} catch (refreshError) {
				TokenStorage.clearAccessToken();
				// Tell AuthContext the session is over so the page logs out instead of
				// staying "signed in" while every poller keeps hitting 401.
				if (getHttpStatus(refreshError) === 401) {
					sessionHint.clear();
					emitSessionExpired("refresh-rejected");
				}
			}
		}

		try {
			const cfg = (error?.config ?? {}) as {
				method?: string;
				url?: string;
				metadata?: { startedAt?: number };
			};
			const startedAt = cfg.metadata?.startedAt;
			const loadingTime = typeof startedAt === "number" ? Math.round(performance.now() - startedAt) : null;

			const status = error?.response?.status ?? null;
			// Skip logging for our own telemetry endpoint to avoid recursive loops
			// if the backend is ever unreachable.
			if (!cfg.url?.endsWith("/logs/batch")) {
				userLogClient.logEvent({
					action: LogAction.API_ERROR,
					result_status: LogResultStatus.FAIL,
					error_code: status != null ? String(status) : (error?.code ?? "network"),
					loading_time: loadingTime,
					properties: {
						method: (cfg.method || "").toUpperCase() || null,
						url: cfg.url ?? null,
						// `error.message` carries axios' human message (e.g. "Network Error"
						// or "timeout of 30000ms exceeded").
						message: error?.message ?? null,
					},
				});
			}
		} catch {
			// Absolutely must not change existing axios error-propagation behavior.
		}
		// Preserve original behavior: propagate the rejection so callers /
		// AuthContext can handle refresh etc.
		return Promise.reject(error);
	},
);
